import { ImportHistoryApi } from '../../../../api/importHistoryApi';
import { downloadFile, getFileNameFromHeader } from '../../../../api/reportSettingsApi';
import { PageState } from '../../../../interfaces/Pagination';
import { SnackbarParameters } from '../../../../interfaces/Snackbar';
import { SummaryData } from '../../../../interfaces/SummaryData';
import withSnackbar from '../../../../shared/components/withSnackbar';
import { INITIAL_PAGE_STATE } from '../../../../shared/constants/constants';
import { summaryTableColumn } from '../../../../shared/helpers/summaryHelper';
import { AxiosResponse } from 'axios';
import { SovosCircularProgress, SovosDivider, SovosIconButton, SovosPaginationFooter, SovosTable } from 'mosaic-react';
import { Download } from 'mosaic-react-icons';
import { useEffect, useState } from 'react';

interface SummaryProps {
	processId: string;
	showSnackbar: (value: SnackbarParameters) => void;
}

const Summary = (props: SummaryProps) => {
	const { processId, showSnackbar } = props;
	const [importSummary, setImportSummary] = useState<SummaryData[]>();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [pageState, setPageState] = useState<PageState>({ ...INITIAL_PAGE_STATE, perPage: 100 });

	useEffect(() => {
		let mounted = true;
		setIsLoading(true);

		ImportHistoryApi.getSummaryByProcessId(processId, pageState.page - 1, pageState.perPage)
			.then(response => {
				if (mounted) {
					const data = response.data;
					setImportSummary(data.content);

					const pageable = data.pageable;
					const totalEntries = data.totalElements;
					setPageState({
						page: pageable.pageNumber + 1,
						perPage: pageable.pageSize,
						totalEntries,
					});
				}
			})
			.catch(error => console.log('Error: ', error))
			.finally(() => setIsLoading(false));
		return () => {
			mounted = false;
		};
	}, [processId, pageState.perPage, pageState.page]);

	const handleExport = (): void => {
		ImportHistoryApi.getProcess(processId)
			.then(response => {
				handleStatusResponses(response);
			})
			.catch(error => console.log('Error: ', error));
	};

	const handleDownloadSummary = (): void => {
		ImportHistoryApi.getProcessSummaryByProcessId(processId)
			.then(response => {
				handleStatusResponses(response);
			})
			.catch(error => console.log('Error: ', error));
	};

	const handleStatusResponses = (response: AxiosResponse): void => {
		if (response.status === 201) {
			showSnackbar({ message: 'Your report is being generated.', variant: 'confirmation' });
		} else if (response.status === 206) {
			showSnackbar({
				message: 'Your report is still being generated. Please wait a few minutes and try again',
				variant: 'warning',
			});
		} else if (response.status === 200) {
			showSnackbar({
				message: 'Report generation was successful, download will start shortly',
				variant: 'confirmation',
			});
			downloadFile(getFileNameFromHeader(response.headers), new Blob([response.data]));
		} else if (response.data.errors) {
			showSnackbar({ message: response.data.errors[0].message, variant: 'error' });
		} else {
			showSnackbar({ message: 'Unable to Export. Please try again later.', variant: 'error' });
		}
	};

	const handlePageChange = (newPage: number, newItemsPerPage: number): void => {
		setPageState({ ...pageState, page: newPage, perPage: newItemsPerPage });
	};

	return (
		<div>
			<div>
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<p className='process-box__tax-amount-message' style={{ marginRight: 5 }}>
						Tax amounts:
					</p>
					<span className='info-container__file' onClick={handleExport}>
						Download report
					</span>
				</div>
				<p style={{ width: '50%', margin: 0, color: '#a0a3af' }}>
					The Exempt Amount column show only the state-level exempt amount totals. To view the exempt amount applied on
					the local taxing jurisdiction levels such as county or city download the full report.
				</p>
			</div>
			<SovosDivider style={{ marginTop: 10 }} />
			<div style={{ display: 'flex', justifyContent: 'end' }}>
				<SovosIconButton
					onClick={handleDownloadSummary}
					tooltipText='Download Summary'
					style={{ borderLeft: '1px solid', borderRadius: 1, borderColor: '#0000001a' }}
				>
					<Download style={{ width: 20 }} />
				</SovosIconButton>
			</div>
			<SovosDivider />
			{isLoading ? (
				<div style={{ display: 'flex', justifyContent: 'center', marginTop: 50 }}>
					<SovosCircularProgress />
				</div>
			) : (
				<>
					{importSummary && importSummary.length > 0 && (
						<div style={{ marginTop: 20 }}>
							<SovosTable columns={summaryTableColumn} data={importSummary} />
							{pageState.totalEntries > 0 && (
								<SovosPaginationFooter
									currentPage={pageState.page}
									itemsPerPage={pageState.perPage}
									totalItems={pageState.totalEntries}
									onPageChange={handlePageChange}
								/>
							)}
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default withSnackbar(Summary);
